import photo1 from "../Assets/Images/landing/photo-1.jpg";
import photo2 from "../Assets/Images/landing/photo-2.jpg";
import photo3 from "../Assets/Images/landing/photo-3.jpg";
import photo4 from "../Assets/Images/landing/photo-4.jpg";
import photo5 from "../Assets/Images/landing/photo-5.jpg";
import photo6 from "../Assets/Images/landing/photo-6.jpg";

/**
 * Static placeholder blog content -- our real Ghost CMS API (see actions/Blogs/blogsActions.ts)
 * is currently returning no posts, so the /blogs pages fall back to this local data instead of
 * rendering empty. Swap this out (or make it conditional on the API response) once the CMS has
 * real posts again.
 */
export type BlogCategory = "Guides" | "Branding" | "Product" | "Photography" | "Business";
export const BLOG_CATEGORIES: BlogCategory[] = ["Guides", "Branding", "Product", "Photography", "Business"];

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  author: string;
  initials: string;
  authorBio?: string;
  date: string;
  readMin: number;
  cover: string;
  featured?: boolean;
  body?: { heading?: string; paragraphs: string[] }[];
  tags?: string[];
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "protect-your-work-and-brand",
    title: "Protect your work and brand with a watermark app: the ultimate guide to image security",
    excerpt: "From casual creators to full-time studios, watermarking is the simplest way to prove ownership. Here's how to do it well without ruining the photo.",
    category: "Guides",
    author: "Aarav Sharma",
    initials: "AS",
    authorBio: "Photographer and product writer. Covers image tools, licensing and the small habits that protect creative work.",
    date: "Jul 10, 2026",
    readMin: 8,
    cover: photo1,
    featured: true,
    tags: ["watermark", "copyright", "photography", "branding"],
    body: [
      {
        heading: "Why watermarking still matters in 2026",
        paragraphs: [
          "Every day, more than 3 billion photos are uploaded to the internet. A large percentage of them are copied, cropped, or reposted without credit within hours. For photographers, designers and small studios, that means lost attribution, lost licensing revenue, and a slow erosion of brand recognition.",
          "A watermark won't stop a determined thief — nothing will — but it does three important things at once: it discourages casual reposting, it travels with the image wherever it goes, and it plants your name in front of every viewer who sees your work. Done well, it's the cheapest brand marketing you'll ever run.",
        ],
      },
      {
        heading: "What a good watermark actually looks like",
        paragraphs: [
          "The best watermarks share three traits: they are legible, they respect the composition, and they are consistent across a body of work. Legibility means readable at thumbnail size. Respect means it doesn't sit on a face, a horizon, or the subject's eyes. Consistency means the same mark, same corner, same scale — every photo, every platform.",
          "A common mistake is going too big or too bold. A watermark is a signature, not a title card. If your mark is the first thing a viewer notices, the photo has already lost.",
        ],
      },
      {
        heading: "The 3-rule checklist",
        paragraphs: [
          "1. Place it in the calm part of the frame. Corners, negative space, or along a leading edge where the eye doesn't linger.",
          "2. Match the contrast, not the color. A soft white mark on a busy photo reads cleaner than a bold coral fighting the palette.",
          "3. Scale it to the long edge. Roughly 6–10% of the image width is the sweet spot for social crops; go smaller for print.",
        ],
      },
      {
        heading: "Templates: the secret to speed",
        paragraphs: [
          "One-off watermarking works for a handful of images. If you're shipping a wedding gallery, an editorial set, or a week of Instagram, you need templates. Save two variants — light and dark — and let eZy Watermark's layout pick the right one based on the underlying photo.",
          "Templates travel between orientations too. A portrait mark anchored bottom-right will re-anchor cleanly when you swing to landscape, keeping the safe margins intact. This is where batch mode earns its keep: 100 photos, one click, consistent brand.",
        ],
      },
      {
        heading: "What a watermark doesn't do",
        paragraphs: [
          "A watermark is not a legal instrument. It's a signal. If someone strips it and reposts your work, your copyright still exists — the mark simply made ownership obvious and the theft deliberate. Register your important work, keep the RAW files, and treat the watermark as the first, friendliest line of defence.",
        ],
      },
      {
        heading: "Wrapping up",
        paragraphs: [
          "Great watermarking is invisible until it needs to be seen. Pick a mark you're proud of, keep it consistent across your entire portfolio, and let your work speak first. The mark is the quiet reminder that the work is yours.",
        ],
      },
    ],
  },
  {
    slug: "batch-watermark-100-photos",
    title: "How to batch-watermark 100 photos in under a minute",
    excerpt: "A step-by-step walk-through of eZy Watermark's batch mode, template positioning, and the small settings that save hours.",
    category: "Product",
    author: "Meera Iyer",
    initials: "MI",
    authorBio: "Wedding photographer turned workflow nerd.",
    date: "Jul 06, 2026",
    readMin: 5,
    cover: photo2,
    tags: ["batch", "workflow", "templates"],
    body: [
      {
        heading: "Why batch mode changes everything",
        paragraphs: [
          "A wedding delivery is typically 400–800 photos. Watermarking each one by hand isn't just tedious — it's the fastest way to introduce inconsistency. One mark drifts slightly higher, another sits on the bride's dress, and suddenly your gallery looks amateur.",
          "Batch mode fixes this in two ways: every image gets the identical mark, and you can nudge the placement for any outliers before exporting.",
        ],
      },
      {
        heading: "The 4-step workflow",
        paragraphs: [
          "1. Import your folder. Drag-and-drop your images at once — JPG or PNG, mixed orientations are fine.",
          "2. Pick or design a template. Choose a saved template from your library, or design a new one once and save it for the future.",
          "3. Review the preview. eZy shows you sample thumbnails from the batch so you can spot any placement issues before committing.",
          "4. Export. Pick your quality, output folder, and click Go.",
        ],
      },
      {
        heading: "Settings that actually matter",
        paragraphs: [
          "Opacity between 60–80% works for almost every photo. Anchor to a corner, not the center.",
          "For social delivery, add a small safe margin to keep the mark clear of mobile crops. For print delivery, scale the mark down relative to the long edge.",
        ],
      },
      {
        heading: "The time you get back",
        paragraphs: [
          "Photographers save real hours per shoot by moving from manual to batch watermarking. Multiply that across a season and it adds up fast.",
        ],
      },
    ],
  },
  {
    slug: "signature-vs-logo",
    title: "Signature vs logo: which watermark works better for photographers?",
    excerpt: "Both have their place. We break down when a handwritten signature reads as premium and when a clean logo mark travels further.",
    category: "Branding",
    author: "Noah Bennett",
    initials: "NB",
    authorBio: "Brand designer working with photographers and studios.",
    date: "Jul 02, 2026",
    readMin: 6,
    cover: photo3,
    tags: ["branding", "signature", "logo"],
    body: [
      {
        heading: "The tension",
        paragraphs: [
          "Every photographer asks this eventually: should my watermark be my handwritten signature, or a clean typeset logo mark? The answer depends less on taste and more on the kind of work you're selling.",
          "Signatures read as personal, artisanal, editorial. Logos read as professional, scalable, brand-first. Neither is better — they signal different things.",
        ],
      },
      {
        heading: "When a signature wins",
        paragraphs: [
          "Choose a signature if your work is intimate: weddings, family portraits, fine-art prints. A signed image feels like it came from a person, not a company.",
          "The catch: signatures are illegible at small sizes. If most of your delivery is Instagram grid, a signature at 6% of the frame becomes a squiggle.",
        ],
      },
      {
        heading: "When a logo wins",
        paragraphs: [
          "Choose a logo mark if you're building a studio brand, licensing commercial work, or shipping high volume. A wordmark scales cleanly from a thumbnail to a billboard, and it survives compression, screenshots and reposts.",
        ],
      },
      {
        heading: "The hybrid that most pros use",
        paragraphs: [
          "The pragmatic answer: use both. A logo mark on all commercial and social delivery, and a signature reserved for signed prints, portfolios, and hero images on your site. eZy Watermark lets you save both as templates and switch based on the folder you're processing.",
        ],
      },
    ],
  },
  {
    slug: "readable-but-not-loud",
    title: "Readable, but not loud: designing a watermark that doesn't ruin the photo",
    excerpt: "Opacity, scale, corner placement and one contrast rule that keeps your mark visible without stealing the subject.",
    category: "Photography",
    author: "Ishaan Patel",
    initials: "IP",
    authorBio: "Editorial photographer based in Mumbai.",
    date: "Jun 28, 2026",
    readMin: 4,
    cover: photo4,
    tags: ["design", "opacity", "composition"],
    body: [
      {
        heading: "The one rule that fixes 90% of bad watermarks",
        paragraphs: [
          "Match the tone, not the color. A watermark that borrows the darkness of the shadows — not the color of your brand — disappears into the frame while remaining legible on close inspection.",
          "That's why most professional marks are white, black, or a very light grey. Bold accent colors look great in the app; they fight the photo in the wild.",
        ],
      },
      {
        heading: "Opacity, scale, placement",
        paragraphs: [
          "Opacity: 60–80%. Below 50% it disappears on light backgrounds. Above 85% it starts to shout.",
          "Scale: 6–10% of the long edge on social, 3–5% for print.",
          "Placement: bottom-right is the default. Move to bottom-left when the composition weights the right, or when your subject leans that way.",
        ],
      },
      {
        heading: "Test at thumbnail size",
        paragraphs: [
          "Every watermark should survive the thumbnail test. Shrink the image and glance at it. Can you still read the mark? Does it still not compete with the subject? If both are true, ship it.",
        ],
      },
    ],
  },
  {
    slug: "templates-that-scale",
    title: "Templates that scale: reuse one watermark across portrait and landscape",
    excerpt: "Anchor points and safe margins that adapt your template to any canvas.",
    category: "Product",
    author: "Aisha Khan",
    initials: "AK",
    authorBio: "Product lead at eZy Watermark.",
    date: "Jun 22, 2026",
    readMin: 5,
    cover: photo5,
    tags: ["templates", "layout", "product"],
    body: [
      {
        heading: "One template, every canvas",
        paragraphs: [
          "The old way of watermarking meant designing separately for portrait, landscape and square. Templates in eZy Watermark solve this with anchor points: the mark is pinned to a corner, and the safe margin scales with the frame.",
          "Change orientation and the mark re-anchors to the equivalent corner with proportional padding.",
        ],
      },
      {
        heading: "How anchor points work",
        paragraphs: [
          "You pick one of nine anchor points — the corners, edges, or center — and define the padding as a percentage of the frame.",
        ],
      },
      {
        heading: "Saving and sharing templates",
        paragraphs: [
          "Save unlimited templates on the Yearly plan. Studios can share a template pack across a team so every editor delivers the same mark.",
        ],
      },
    ],
  },
  {
    slug: "watermark-for-instagram",
    title: "The Instagram-safe watermark: guidelines for reels, stories and grid posts",
    excerpt: "Where to place, how big to go, and how to avoid the crop zones that eat your logo on mobile.",
    category: "Branding",
    author: "Diego Alvarez",
    initials: "DA",
    authorBio: "Social content strategist.",
    date: "Jun 18, 2026",
    readMin: 7,
    cover: photo6,
    tags: ["instagram", "social", "reels"],
    body: [
      {
        heading: "The crop zones nobody talks about",
        paragraphs: [
          "Instagram's grid preview crops to 1:1. Reels crop to 9:16. Stories add UI on the top and bottom edges. If your watermark sits within the top or bottom safe zone of a story, it will overlap the profile chip or the reply bar.",
        ],
      },
      {
        heading: "Grid posts",
        paragraphs: [
          "For the 1:1 grid, bottom-right is the safest anchor. Aim for roughly 6% of frame width and 80% opacity. This keeps the mark clear of any centered subject and stays readable at profile-thumbnail size.",
        ],
      },
      {
        heading: "Stories",
        paragraphs: [
          "For stories, drop the mark inside the middle safe zone and shrink it down. Anchor to the bottom-center and lift it clear of the reply bar.",
        ],
      },
    ],
  },
  {
    slug: "copyright-basics",
    title: "Copyright basics every photographer should know before posting online",
    excerpt: "A friendly primer on ownership, licensing and what a visible watermark actually protects against.",
    category: "Business",
    author: "Priya Kapoor",
    initials: "PK",
    authorBio: "IP lawyer and part-time film photographer.",
    date: "Jun 12, 2026",
    readMin: 9,
    cover: photo2,
    tags: ["copyright", "licensing", "law"],
    body: [
      {
        heading: "You own it the moment you shoot it",
        paragraphs: [
          "In almost every country, copyright vests in the photographer automatically the moment the shutter clicks. Registration matters for enforcement, but ownership itself is automatic.",
          "This is worth internalising: you already own your work. A watermark isn't creating ownership; it's making ownership visible.",
        ],
      },
      {
        heading: "What licensing actually means",
        paragraphs: [
          "A license is permission to use — not a transfer of ownership. When a client 'buys' a photo, they're almost always buying a license. You still own the file.",
        ],
      },
      {
        heading: "What a watermark protects against",
        paragraphs: [
          "A visible watermark is evidence of intent. If your work is reposted with the mark stripped, you have a much stronger case for willful infringement.",
        ],
      },
      {
        heading: "Practical checklist",
        paragraphs: [
          "1. Watermark everything you post publicly. Consistency matters more than perfection.",
          "2. Register your commercial and portfolio work where available.",
          "3. Store the original files. Original-file ownership is close to unbeatable in a dispute.",
          "4. Include a short license in every client delivery.",
        ],
      },
    ],
  },
  {
    slug: "high-res-export-quality",
    title: "Exporting high-res JPGs without softening: a quality checklist",
    excerpt: "Understand the compression slider, choose the right format, and preserve detail from camera to client.",
    category: "Guides",
    author: "Sofia Rossi",
    initials: "SR",
    authorBio: "Fine-art print specialist.",
    date: "May 30, 2026",
    readMin: 5,
    cover: photo1,
    tags: ["export", "quality", "jpg"],
    body: [
      {
        heading: "Compression is not the enemy",
        paragraphs: [
          "JPG compression gets a bad reputation, but the format is remarkably efficient at preserving detail if you export at the right quality. The trick is knowing where the perceptual cliff is.",
          "For most delivery, quality 85-90 is indistinguishable from quality 100 to the human eye, and the file is meaningfully smaller.",
        ],
      },
      {
        heading: "The two-format rule",
        paragraphs: [
          "For social and web: JPG at high quality, sRGB, a reasonable long edge. That's enough for retina displays without overloading upload bandwidth.",
          "For print and archive: highest quality JPG or PNG, full resolution. Never let your printer pull from the same file you posted to Instagram.",
        ],
      },
      {
        heading: "Watermark first, resize second",
        paragraphs: [
          "Always apply the watermark to the full-resolution file and let the export pipeline handle the resize. Watermarking a pre-resized image doubles the compression artifacts around the mark's edges.",
        ],
      },
    ],
  },
];
